import { useDeferredValue, useState } from "react";
import { Anchor, Plus, Ship } from "lucide-react";
import {
  useArchiveVessel,
  useSaveVessel,
  useSaveVesselCall,
  useVesselCalls,
  useVessels,
  type Vessel,
  type VesselCall,
} from "@/api/vessels";
import { VesselCallForm } from "@/components/forms/VesselCallForm";
import { VesselForm } from "@/components/forms/VesselForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { formatDate } from "@/lib/format";

type VesselEditing = { mode: "new" } | { mode: "edit"; vessel: Vessel } | null;
type CallEditing = { mode: "new" } | { mode: "edit"; call: VesselCall } | null;

function VesselsSection() {
  const [query, setQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<VesselEditing>(null);
  const search = useDeferredValue(query.trim());
  const vessels = useVessels(search, includeArchived);
  const save = useSaveVessel();
  const archive = useArchiveVessel();

  return (
    <section aria-labelledby="vessels-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 id="vessels-heading" className="text-lg font-semibold">
          Vessels
        </h2>
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add vessel
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <Input
          type="search"
          placeholder="Search by name or IMO"
          aria-label="Search vessels"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="sm:max-w-xs"
        />
        <Checkbox
          id="vessels-show-archived"
          label="Show archived"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New vessel" : `Edit ${editing.vessel.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VesselForm
              key={editing.mode === "edit" ? `${editing.vessel.id}-${editing.vessel.version}` : "new"}
              initial={editing.mode === "edit" ? editing.vessel : null}
              submitLabel={editing.mode === "new" ? "Add vessel" : "Save vessel"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.vessel.id, version: editing.vessel.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void vessels.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={archive.error} onReload={() => void vessels.refetch()} />

      {vessels.isPending ? (
        <p className="text-muted-foreground">Loading vessels…</p>
      ) : vessels.isError ? (
        <RequestError error={vessels.error} />
      ) : vessels.data.length === 0 ? (
        <EmptyState
          icon={Anchor}
          title={search ? "No vessels match your search" : "No vessels yet"}
          description="Add a vessel with its IMO number before recording its calls."
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {vessels.data.map((vessel) => {
            const isArchived = vessel.archived_at !== null;
            return (
              <li key={vessel.id}>
                <Card className="h-full">
                  <CardContent className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{vessel.name}</span>
                      {isArchived && <Badge variant="muted">Archived</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">IMO {vessel.imo}</p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {!isArchived && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing({ mode: "edit", vessel })}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={archive.isPending}
                        onClick={() =>
                          archive.mutate({
                            id: vessel.id,
                            archived: !isArchived,
                            version: vessel.version,
                          })
                        }
                      >
                        {isArchived ? "Restore" : "Archive"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function VesselCallsSection() {
  const [editing, setEditing] = useState<CallEditing>(null);
  const calls = useVesselCalls({ limit: 50 });
  const save = useSaveVesselCall();

  return (
    <section aria-labelledby="calls-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="calls-heading" className="text-lg font-semibold">
            Vessel calls
          </h2>
          <p className="text-sm text-muted-foreground">
            Each call at a terminal is invoiced once, after departure.
          </p>
        </div>
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            New vessel call
          </Button>
        )}
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new"
                ? "New vessel call"
                : `Edit ${editing.call.vessel_name} (${formatDate(editing.call.departure)})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VesselCallForm
              key={editing.mode === "edit" ? `${editing.call.id}-${editing.call.version}` : "new"}
              initial={editing.mode === "edit" ? editing.call : null}
              submitLabel={editing.mode === "new" ? "Add vessel call" : "Save vessel call"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.call.id, version: editing.call.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void calls.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      {calls.isPending ? (
        <p className="text-muted-foreground">Loading vessel calls…</p>
      ) : calls.isError ? (
        <RequestError error={calls.error} />
      ) : calls.data.length === 0 ? (
        <EmptyState
          icon={Ship}
          title="No vessel calls yet"
          description="Record a call with its terminal, arrival, departure and planning dates."
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3 md:hidden">
            {calls.data.map((call) => (
              <li key={call.id}>
                <Card>
                  <CardContent className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{call.vessel_name}</span>
                      <Badge>{call.client_code}</Badge>
                    </div>
                    <p className="text-sm">{call.terminal}</p>
                    <p className="text-xs text-muted-foreground">
                      Arrival {formatDate(call.arrival)} · Departure {formatDate(call.departure)}
                      {call.voyage ? ` · Voyage ${call.voyage}` : ""}
                    </p>
                    <div className="pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ mode: "edit", call })}
                      >
                        Edit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
          <div className="table-frame hidden md:block">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vessel</th>
                  <th>Client</th>
                  <th>Terminal</th>
                  <th>Arrival</th>
                  <th>Departure</th>
                  <th>Voyage</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {calls.data.map((call) => (
                  <tr key={call.id}>
                    <td className="font-medium">
                      {call.vessel_name}
                      <span className="block text-xs text-muted-foreground">
                        IMO {call.vessel_imo}
                      </span>
                    </td>
                    <td>{call.client_code}</td>
                    <td>{call.terminal}</td>
                    <td className="whitespace-nowrap">{formatDate(call.arrival)}</td>
                    <td className="whitespace-nowrap">{formatDate(call.departure)}</td>
                    <td>{call.voyage || "—"}</td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ mode: "edit", call })}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export function VesselsPage() {
  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Vessels"
        description="Vessels you plan for and their calls at each terminal."
      />
      <VesselsSection />
      <VesselCallsSection />
    </div>
  );
}
